import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";
import kraftonKeyArt from "@/assets/blog/palworld-mobile-krafton-key-art.webp.asset.json";

const URL = "https://palstars.com/guides/palworld-mobile-krafton";
const TITLE = "Krafton Mobile Release Date: Palworld Mobile";
const DESCRIPTION =
  "Krafton mobile release date status for Palworld Mobile by PUBG Studios, how it differs from the Garena beta, and mobile pal catch rate calculators to plan with.";

const FAQ: FaqItem[] = [
  {
    q: "What is the Krafton mobile release date for Palworld Mobile?",
    a: "Krafton has committed to a worldwide 2026 release for Palworld Mobile but has not published a day or month. The game was announced in October 2025 and shown publicly for the first time at G-STAR 2025 in Busan, where visitors played a hands-on demo.",
  },
  {
    q: "Who is developing Palworld Mobile for Krafton?",
    a: "PUBG Studios, Krafton's internal creative studio behind PUBG: Battlegrounds, is building Palworld Mobile under an IP license granted by Pocketpair. Krafton is both developer and publisher for this version.",
  },
  {
    q: "Is Krafton's Palworld Mobile the same as the Garena beta?",
    a: "No. They are two separate licensed games. Krafton's Palworld Mobile is a mobile-optimized take on the original survival game with new strategic battle content, while Garena's Palworld Online is a mobile MMORPG currently in Korea-first pre-registration and closed beta.",
  },
  {
    q: "How do you play the Palworld mobile beta from Krafton?",
    a: "There is no public Krafton beta right now. The only hands-on access so far was the G-STAR 2025 show demo. Watch Krafton's own channels for a test signup; store pages and invite waves are the first real signals, not third-party download links.",
  },
  {
    q: "Will catch rates carry over from the PC game?",
    a: "Krafton has said Palworld Mobile inherits Pal collection, survival and crafting from the original, but no mobile capture tables have been published. Use our sphere odds tools as a planning baseline and expect mobile-specific tuning.",
  },
];

export const Route = createFileRoute("/guides/palworld-mobile-krafton")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Palworld Guides", "/guides"],
        ["Palworld Mobile by Krafton", "/guides/palworld-mobile-krafton"],
      ]),
      faqSchema(FAQ, URL),
    ],
  }),
  component: KraftonMobileGuide,
});

function KraftonMobileGuide() {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-8">
      <nav className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>{" "}
        /{" "}
        <Link to="/guides" className="hover:text-foreground">
          Guides
        </Link>{" "}
        / <span className="text-foreground">Palworld Mobile by Krafton</span>
      </nav>

      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld · mobile</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">
          Palworld Mobile by Krafton: Release Date and What PUBG Studios Is Building
        </h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          The Krafton mobile release date sits inside a worldwide 2026 window with no exact day announced. Krafton is
          developing and publishing Palworld Mobile through PUBG Studios under a Pocketpair IP license, and the first
          playable demo appeared at G-STAR 2025 in Busan. Our{" "}
          <Link to="/guides" className="text-primary hover:underline">
            Palworld tier list guides
          </Link>{" "}
          remain the reference for the Pal roles this version is built on.
        </p>
      </header>

      <figure className="overflow-hidden rounded-xl border border-border/60 bg-background/40">
        <img
          src={kraftonKeyArt.url}
          alt="Palworld Mobile key art showing a trainer and Pals bursting out of a phone screen"
          width={1280}
          height={720}
          loading="lazy"
          decoding="async"
          className="aspect-video w-full object-cover"
        />
        <figcaption className="px-4 py-2 text-xs text-muted-foreground">
          Palworld Mobile key art — © Pocketpair, developed and published on mobile by Krafton.
        </figcaption>
      </figure>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">Krafton mobile release date: what is actually confirmed</h2>
        <p className="aeo-answer">
          Krafton announced Palworld Mobile in October 2025 and stated a worldwide 2026 release. The first public,
          playable build ran at G-STAR 2025 in Busan from November 13 to 16, 2025. Nothing narrower than "2026" has been
          committed, so a precise launch day you find online is a guess.
        </p>
        <p>
          Krafton's route to this project started earlier: PUBG Studios stood up a dedicated Palworld Mobile production
          team in 2024 and secured the license from Pocketpair, then hired combat and platform engineers through 2026
          job postings that describe Unreal-based client and dedicated-server work. That is a live production, not a
          concept pitch.
        </p>
      </section>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">What Krafton says the game keeps and adds</h2>
        <ul className="space-y-2">
          <li>Pal collection and raising carried over from the original game.</li>
          <li>Open-world survival and crafting rebuilt for touch controls.</li>
          <li>New "strategic battle" content built around individual Pal skills.</li>
          <li>Dedicated-server multiplayer work, based on Krafton's own engineering roles.</li>
        </ul>
        <p>
          The honest read is that Krafton is aiming closer to the PC experience than to a spin-off, then layering on
          battle modes that suit short mobile sessions. Capture behavior is one of the systems being re-tuned, so plan
          with the base numbers: our{" "}
          <Link to="/pal-catch" className="text-primary hover:underline">
            mobile Pal catch rate calculators
          </Link>{" "}
          and{" "}
          <Link to="/capture-rates" className="text-primary hover:underline">
            capture rate table
          </Link>{" "}
          still give you the right shape of the odds.
        </p>
      </section>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">Krafton versus Garena: two different mobile games</h2>
        <p>
          Both companies hold Pocketpair licenses, and both are shipping mobile Palworld products in the same window.
          Krafton's Palworld Mobile is a mobile-optimized survival game from PUBG Studios. Garena's Palworld Online is an
          MMORPG already in Korea-first pre-registration and closed beta — if you searched for a beta download, that is
          the one you want, covered in the{" "}
          <Link to="/guides/palworld-mobile-garena" className="text-primary hover:underline">
            Palworld Mobile by Garena guide
          </Link>
          . For the creature data behind both, start with{" "}
          <Link to="/pals" className="text-primary hover:underline">
            all Palworld pals
          </Link>
          .
        </p>
      </section>

      <FaqSection items={FAQ} heading="Palworld Mobile by Krafton FAQ" />

      <section className="text-xs text-muted-foreground space-y-1">
        <h2 className="text-sm font-semibold text-foreground">Sources</h2>
        <p>
          <a
            href="https://palworldm.com/en"
            rel="nofollow ugc noopener noreferrer"
            target="_blank"
            className="text-primary hover:underline"
          >
            Palworld Mobile — official site
          </a>
        </p>
        <p>
          <a
            href="https://www.pocketpair.jp/"
            rel="nofollow ugc noopener noreferrer"
            target="_blank"
            className="text-primary hover:underline"
          >
            Pocketpair — official site
          </a>
        </p>
      </section>
    </div>
  );
}
